import React, { useState, useEffect } from 'react';
import { Settings, ToggleLeft, ToggleRight, Activity, AlertCircle, ExternalLink, Save, RefreshCw, FileText, ChevronDown, ChevronUp, LayoutDashboard, TableProperties, ClipboardList, Link, Upload, Shield, Mail, Hammer, HardHat, Cog, Building2, Table } from 'lucide-react';
import api from '../services/api';
import { WidgetChecklist } from '../components/widgets';
import WidgetDossierConfig from '../components/widgets/WidgetDossierConfig';
import WidgetSuiviConfig from '../components/widgets/WidgetSuiviConfig';
import WidgetDashboardConfig from '../components/widgets/WidgetDashboardConfig';
import WidgetDossiersListConfig from '../components/widgets/WidgetDossiersListConfig';
import WidgetDetailDossierConfig from '../components/widgets/WidgetDetailDossierConfig';
import WidgetClientLinkConfig from '../components/widgets/WidgetClientLinkConfig';
import WidgetClientDocumentsConfig from '../components/widgets/WidgetClientDocumentsConfig';
import WidgetDiagnosticConfig from '../components/widgets/WidgetDiagnosticConfig';
import WidgetEmailTemplates from '../components/widgets/WidgetEmailTemplates';
import WidgetTravauxConfig from '../components/widgets/WidgetTravauxConfig';
import WidgetInstallateursConfig from '../components/widgets/WidgetInstallateursConfig';
import WidgetAMOConfig from '../components/widgets/WidgetAMOConfig';
import WidgetTableauConfig from '../components/widgets/WidgetTableauConfig';
import WidgetParametresGeneraux from '../components/widgets/WidgetParametresGeneraux';
import WidgetLiensPartenaires from '../components/widgets/WidgetLiensPartenaires';

const WidgetsAdmin = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWidget, setEditingWidget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      const res = await api.get('/widgets');
      setWidgets(res.data);
    } catch (error) {
      console.error('Erreur chargement widgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleWidget = async (widgetId) => {
    try {
      const res = await api.patch(`/widgets/${widgetId}/toggle`);
      setWidgets(widgets.map(w => 
        w.id === widgetId ? { ...w, actif: res.data.actif } : w
      ));
    } catch (error) {
      console.error('Erreur toggle widget:', error);
      alert('Erreur lors de la modification');
    }
  };

  const saveWidgetConfig = async (widgetId, config) => {
    setSaving(true);
    try {
      await api.patch(`/widgets/${widgetId}`, { configuration: config });
      await loadWidgets();
      setEditingWidget(null);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🔌 Gestion des Widgets</h1>
          <p className="text-gray-600 mt-1">Configurez et activez les modules de votre application</p>
        </div>
        <button
          onClick={loadWidgets}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('checklist')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'checklist'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              Checklist
            </button>
            <button
              onClick={() => setActiveTab('travaux')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'travaux'
                  ? 'border-orange-600 text-orange-600 bg-orange-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Hammer className="w-4 h-4" />
              Travaux
            </button>
            <button
              onClick={() => setActiveTab('installateurs')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'installateurs'
                  ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <HardHat className="w-4 h-4" />
              Installateurs
            </button>
            <button
              onClick={() => setActiveTab('amo')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'amo'
                  ? 'border-teal-600 text-teal-600 bg-teal-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Building2 className="w-4 h-4" />
              AMO
            </button>
            <button
              onClick={() => setActiveTab('tableau')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'tableau'
                  ? 'border-slate-600 text-slate-600 bg-slate-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Table className="w-4 h-4" />
              Tableau
            </button>
            <button
              onClick={() => setActiveTab('dossier')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'dossier'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              Fiche
            </button>
            <button
              onClick={() => setActiveTab('suivi')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'suivi'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Activity className="w-4 h-4" />
              Suivi
            </button>
            <button
              onClick={() => setActiveTab('liste-dossiers')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'liste-dossiers'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-liste-dossiers"
            >
              <TableProperties className="w-4 h-4" />
              Liste
            </button>
            <button
              onClick={() => setActiveTab('detail-dossier')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'detail-dossier'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-detail-dossier"
            >
              <ClipboardList className="w-4 h-4" />
              Détail
            </button>
            <button
              onClick={() => setActiveTab('client-link')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'client-link'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-client-link"
            >
              <Link className="w-4 h-4" />
              Lien Client
            </button>
            <button
              onClick={() => setActiveTab('liens-partenaires')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'liens-partenaires'
                  ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-liens-partenaires"
            >
              <ExternalLink className="w-4 h-4" />
              Liens Partenaires
            </button>
            <button
              onClick={() => setActiveTab('client-documents')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'client-documents'
                  ? 'border-purple-600 text-purple-600 bg-purple-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-client-documents"
            >
              <Upload className="w-4 h-4" />
              Docs
            </button>
            <button
              onClick={() => setActiveTab('diagnostic')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'diagnostic'
                  ? 'border-emerald-600 text-emerald-600 bg-emerald-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-diagnostic"
            >
              <Shield className="w-4 h-4" />
              Diagnostic
            </button>
            <button
              onClick={() => setActiveTab('email-templates')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'email-templates'
                  ? 'border-emerald-600 text-emerald-600 bg-emerald-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-email-templates"
            >
              <Mail className="w-4 h-4" />
              Emails
            </button>
            <button
              onClick={() => setActiveTab('parametres')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'parametres'
                  ? 'border-gray-600 text-gray-700 bg-gray-100'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-parametres"
            >
              <Cog className="w-4 h-4" />
              Paramètres
            </button>
            <button
              onClick={() => setActiveTab('autres')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === 'autres'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings className="w-4 h-4" />
              Autres
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Tab Dashboard */}
          {activeTab === 'dashboard' && <WidgetDashboardConfig />}

          {/* Tab Checklist */}
          {activeTab === 'checklist' && <WidgetChecklist isAdmin={true} />}

          {/* Tab Travaux */}
          {activeTab === 'travaux' && <WidgetTravauxConfig />}

          {/* Tab Installateurs */}
          {activeTab === 'installateurs' && <WidgetInstallateursConfig />}

          {/* Tab AMO */}
          {activeTab === 'amo' && <WidgetAMOConfig />}

          {/* Tab Tableau */}
          {activeTab === 'tableau' && <WidgetTableauConfig />}

          {/* Tab Dossier */}
          {activeTab === 'dossier' && <WidgetDossierConfig />}

          {/* Tab Suivi */}
          {activeTab === 'suivi' && <WidgetSuiviConfig />}

          {/* Tab Liste Dossiers */}
          {activeTab === 'liste-dossiers' && <WidgetDossiersListConfig />}

          {/* Tab Détail Dossier */}
          {activeTab === 'detail-dossier' && <WidgetDetailDossierConfig />}

          {/* Tab Lien Client */}
          {activeTab === 'client-link' && <WidgetClientLinkConfig />}

          {/* Tab Liens Partenaires */}
          {activeTab === 'liens-partenaires' && <WidgetLiensPartenaires />}

          {/* Tab Documents Client */}
          {activeTab === 'client-documents' && <WidgetClientDocumentsConfig />}

          {/* Tab Diagnostic */}
          {activeTab === 'diagnostic' && <WidgetDiagnosticConfig />}

          {/* Tab Email Templates */}
          {activeTab === 'email-templates' && <WidgetEmailTemplates />}

          {/* Tab Paramètres Généraux */}
          {activeTab === 'parametres' && <WidgetParametresGeneraux />}

          {/* Tab Autres Widgets */}
          {activeTab === 'autres' && (
            <div className="space-y-6">
              {/* Stats globales */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <div className="text-4xl font-bold mb-2">{widgets.length}</div>
                  <div className="text-green-100">Widgets disponibles</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <div className="text-4xl font-bold mb-2">{widgets.filter(w => w.actif).length}</div>
                  <div className="text-blue-100">Widgets actifs</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                  <div className="text-4xl font-bold mb-2">
                    {widgets.reduce((sum, w) => sum + (w.stats?.utilisations || 0), 0)}
                  </div>
                  <div className="text-purple-100">Total utilisations</div>
                </div>
              </div>

              {/* Liste des widgets - seulement ceux qui ne sont pas dans les autres onglets */}
              <div className="space-y-4">
        {widgets
          .filter(w => !['checklist-documents', 'fiche-dossier', 'configuration-suivi'].includes(w.code))
          .map((widget) => (
          <div 
            key={widget.id} 
            className={`bg-white rounded-xl shadow-sm border-2 transition ${
              widget.actif ? 'border-green-200' : 'border-gray-200'
            }`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
                    widget.actif ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {widget.icone}
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-gray-900">{widget.nom}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        widget.actif 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {widget.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-1">{widget.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Activity className="w-4 h-4" />
                        {widget.stats?.utilisations || 0} utilisations
                      </span>
                      {widget.stats?.erreurs > 0 && (
                        <span className="flex items-center gap-1 text-red-500">
                          <AlertCircle className="w-4 h-4" />
                          {widget.stats.erreurs} erreurs
                        </span>
                      )}
                      {widget.configuration?.url && (
                        <a 
                          href={widget.configuration.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Voir le site
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setEditingWidget(editingWidget === widget.id ? null : widget.id)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    title="Configurer"
                    data-testid={`config-widget-${widget.code}`}
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => toggleWidget(widget.id)}
                    className={`p-2 rounded-lg transition ${
                      widget.actif 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={widget.actif ? 'Désactiver' : 'Activer'}
                    data-testid={`toggle-widget-${widget.code}`}
                  >
                    {widget.actif ? (
                      <ToggleRight className="w-8 h-8" />
                    ) : (
                      <ToggleLeft className="w-8 h-8" />
                    )}
                  </button>
                </div>
              </div>

              {/* Panel de configuration */}
              {editingWidget === widget.id && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4">⚙️ Configuration</h4>
                  
                  {/* Configuration spécifique Checklist Documents */}
                  {widget.code === 'checklist-documents' && (
                    <ChecklistConfig />
                  )}
                  
                  {/* Configuration spécifique Fiche Dossier */}
                  {widget.code === 'fiche-dossier' && (
                    <DossierConfig />
                  )}
                  
                  {/* Configuration spécifique Suivi */}
                  {widget.code === 'configuration-suivi' && (
                    <SuiviConfig />
                  )}
                  
                  {/* Configuration spécifique AGIRC-ARRCO */}
                  {widget.code === 'agirc-arrco' && (
                    <AgircArrcoConfig 
                      widget={widget}
                      onSave={(config) => saveWidgetConfig(widget.id, config)}
                      saving={saving}
                    />
                  )}
                  
                  {/* Configuration générique pour autres widgets */}
                  {!['agirc-arrco', 'checklist-documents', 'fiche-dossier', 'configuration-suivi'].includes(widget.code) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-2">Configuration actuelle :</p>
                      <pre className="text-sm text-gray-600 overflow-auto bg-white p-3 rounded border">
                        {JSON.stringify(widget.configuration, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {widgets.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-6xl mb-4">🔌</div>
            <p>Aucun widget disponible</p>
          </div>
        )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant de configuration pour Checklist Documents (MODE ADMIN)
const ChecklistConfig = () => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h5 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Gestion des types de documents
        </h5>
        <p className="text-sm text-blue-700">
          Configurez les documents requis pour les dossiers. Utilisez l'icône <strong>œil</strong> pour rendre un document visible ou non dans la checklist des fiches client.
        </p>
      </div>
      
      {/* Widget Checklist en mode Admin */}
      <WidgetChecklist isAdmin={true} />
    </div>
  );
};

// Composant de configuration pour Fiche Dossier (MODE ADMIN)
const DossierConfig = () => {
  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
        <h5 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Configuration de la fiche dossier
        </h5>
        <p className="text-sm text-purple-700">
          Configurez les sections et champs affichés dans l'onglet "Dossier" de la fiche client. 
          Utilisez l'icône <strong>œil</strong> pour masquer/afficher des sections ou des champs.
          Réorganisez l'ordre des sections avec les flèches.
        </p>
      </div>
      
      {/* Widget Config Dossier */}
      <WidgetDossierConfig />
    </div>
  );
};

// Composant de configuration pour Suivi (MODE ADMIN)
const SuiviConfig = () => {
  return (
    <div className="space-y-4">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
        <h5 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Configuration des types d'actions
        </h5>
        <p className="text-sm text-orange-700">
          Configurez les types d'actions disponibles dans le suivi (appels, emails, courriers...). 
          Les types <strong>système</strong> sont générés automatiquement et ne peuvent pas être supprimés.
        </p>
      </div>
      
      {/* Widget Config Suivi */}
      <WidgetSuiviConfig />
    </div>
  );
};

// Composant de configuration spécifique pour AGIRC-ARRCO
const AgircArrcoConfig = ({ widget, onSave, saving }) => {
  const [config, setConfig] = useState(widget.configuration || {});

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL du site DOPLI
        </label>
        <input
          type="url"
          value={config.url || ''}
          onChange={(e) => setConfig({ ...config, url: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="https://espace-personnel.agirc-arrco.fr/public/dopli"
        />
        <p className="text-sm text-gray-500 mt-1">
          URL utilisée pour rechercher la caisse de retraite
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Champs requis
        </label>
        <div className="flex flex-wrap gap-2">
          {(config.champs_requis || []).map((champ, idx) => (
            <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
              {champ}
            </span>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Champs nécessaires pour effectuer la recherche
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h5 className="font-medium text-yellow-800 mb-2">💡 Comment ça fonctionne</h5>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• L'utilisateur remplit: Nom, Prénom, N° SS (13 chiffres), Clé (2 chiffres)</li>
          <li>• Le système interroge automatiquement le site AGIRC-ARRCO</li>
          <li>• La caisse trouvée est auto-remplie dans le formulaire</li>
          <li>• Si la caisse n'existe pas, elle est créée automatiquement</li>
        </ul>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => onSave(config)}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default WidgetsAdmin;
